import {
  Fragment, useCallback, useEffect, useId, useRef, useState, type KeyboardEvent,
} from 'react'
import { useWidget } from '../../../providers/widget'
import { usePdpRoute } from '../../../common/use-pdp-route'
import { useFetchWidget } from '../../../hooks'
import { usePropertyHrefFeed } from '../../../common/use-property-href-feed'
import type { PropertyHrefFeed } from '../../../common/property-href'
import { getProxiedImageUrl } from '../../../common/image-proxy'
import { buildGridPropertyUrl } from '../../../common/property-card/explorer-template-bridge'
import type { ExploreWidgetConfig } from '../config'
import { useExplorer } from '../providers'
import { useCompare, type CompareApi } from './CompareProvider'
import { buildRows, withShareParam, type CompareRow, type CompareSnap } from './model'
import './compare.css'

/**
 * Compare Homes UI (#1713): the toggle in the results header, the floating
 * pill, the bottom tray and the comparison dialog. All of it is client state:
 * nothing here renders before the guest acts, except the header toggle, whose
 * markup depends only on the settings (so the server and the client agree).
 */

function ColumnsIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      {[2.5, 11.3].map((x) => (
        <rect key={x} x={x} y="3.5" width="6.2" height="13" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
      ))}
    </svg>
  )
}

function HomeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.5 9.5L10 4l6.5 5.5M5 8.5V16h10V8.5" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 10.5l3.8 3.8 7.2-8" />
    </svg>
  )
}

// -- the way in ---------------------------------------------------------------------

/**
 * The list-level toggle, in the results header beside the sort control. It
 * tells the floating pill whether it is on screen, so the pill only stands in
 * while the header toggle is scrolled away.
 */
export function CompareToggle() {
  const api = useCompare()
  const ref = useRef<HTMLButtonElement>(null)
  const report = api?.reportEntry
  const present = !!api

  useEffect(() => {
    const el = ref.current
    if (!report || !el) return
    report(true, true)
    let io: IntersectionObserver | null = null
    if (typeof IntersectionObserver === 'function') {
      try {
        io = new IntersectionObserver((entries) => {
          report(true, entries[entries.length - 1].isIntersecting)
        })
        io.observe(el)
      } catch {
        // No observer: the toggle counts as in view, so the pill stays away rather than doubling it.
        io = null
      }
    }
    return () => {
      io?.disconnect()
      report(false, false)
    }
  }, [report, present])

  if (!api) return null
  return (
    <button
      ref={ref}
      type="button"
      className="hrc-entry hrc-entry--inline"
      data-hr-compare-toggle=""
      aria-pressed={api.mode}
      // A settled list with fewer than two homes has nothing to compare.
      disabled={!api.mode && api.settled && api.rowCount < 2}
      style={api.accentStyle}
      onClick={() => api.setMode(!api.mode)}
    >
      <ColumnsIcon />
      <span className="hrc-entry__text">{api.buttonText}</span>
    </button>
  )
}

function CompareTray({ api, openRef, onOpen }: {
  api: CompareApi
  openRef: React.RefObject<HTMLButtonElement | null>
  onOpen: () => void
}) {
  const L = api.labels
  const count = api.selected.length
  const notice = api.notice
  const message = notice?.kind === 'max'
    ? L.maxReached(api.max)
    : notice?.kind === 'missing'
      ? L.missing(notice.count)
      : count >= 2
        ? L.ofMax(count, api.max)
        : count === 1
          ? L.oneMore
          : L.modeHint(api.buttonText, api.max)

  return (
    <div className="hrc-tray" role="region" aria-label={L.title} data-hr-compare-tray="" style={api.accentStyle}>
      <div className="hrc-tray__lead">
        <ColumnsIcon />
        <span className="hrc-tray__count" aria-live="polite" data-notice={notice ? '' : undefined}>{message}</span>
      </div>
      <div className="hrc-tray__body">
        <div className="hrc-tray__slots">
          {Array.from({ length: api.max }, (_, i) => {
            const slug = api.selected[i]
            if (!slug) {
              return (
                <div key={`empty-${i}`} className="hrc-slot is-empty" aria-hidden="true">
                  <span className="hrc-slot__thumb"><HomeIcon /></span>
                </div>
              )
            }
            const snap = api.snaps[slug]
            const title = snap?.title ?? ''
            return (
              <div key={slug} className="hrc-slot is-filled">
                <span className="hrc-slot__thumb">
                  {snap?.image ? <img src={getProxiedImageUrl(snap.image, 96, 96)} alt="" loading="lazy" /> : null}
                </span>
                <span className="hrc-slot__name">{title}</span>
                <button type="button" className="hrc-slot__remove" aria-label={L.removeHome(title)} onClick={() => api.remove(slug)}>
                  {'×'}
                </button>
              </div>
            )
          })}
        </div>
        <div className="hrc-tray__actions">
          <button type="button" className="hrc-btn hrc-btn--ghost" data-hr-compare-clear="" onClick={api.clear}>
            {L.clear}
          </button>
          <button ref={openRef} type="button" className="hrc-btn hrc-btn--primary" data-hr-compare-open="" disabled={count < 2} onClick={onOpen}>
            {api.buttonText}
          </button>
        </div>
      </div>
    </div>
  )
}

// -- the dialog ------------------------------------------------------------------------

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'

function activeWithin(node: Node | null): HTMLElement | null {
  const root = node?.getRootNode?.() as Document | ShadowRoot | undefined
  return ((root && 'activeElement' in root ? root.activeElement : null) as HTMLElement | null) ?? null
}

function copyByCommand(url: string, host: HTMLElement | null): boolean {
  try {
    if (typeof document.execCommand !== 'function') return false
    const box = document.createElement('textarea')
    box.value = url
    box.setAttribute('readonly', '')
    box.setAttribute('aria-hidden', 'true')
    box.style.position = 'fixed'
    box.style.top = '0'
    box.style.left = '-9999px'
    ;(host ?? document.body).appendChild(box)
    box.select()
    const ok = document.execCommand('copy')
    box.parentNode?.removeChild(box)
    return !!ok
  } catch {
    return false
  }
}

function CompareDialog({ api, pdpRoute, hrefFeed }: { api: CompareApi; pdpRoute: string; hrefFeed: PropertyHrefFeed | undefined }) {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { state } = useExplorer()
  const L = api.labels
  const titleId = `hrc-title-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`
  const panelRef = useRef<HTMLDivElement>(null)
  const [message, setMessage] = useState('')
  const messageTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const snaps = api.selected.map((slug) => api.snaps[slug]).filter((s): s is CompareSnap => !!s)
  const rows = buildRows(snaps, L)
  const newTab = options.card?.openInNewTab === true

  // The dialog owns the keyboard: focus lands in it, and the page behind stops scrolling.
  useEffect(() => {
    panelRef.current?.focus()
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
      clearTimeout(messageTimer.current)
    }
  }, [])

  const { closeTable } = api
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') closeTable()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [closeTable])

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const panel = panelRef.current
    if (!panel) return
    const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (focusables.length === 0) {
      e.preventDefault()
      panel.focus()
      return
    }
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = activeWithin(panel)
    const outside = !active || active === panel || !panel.contains(active)
    if (e.shiftKey && (outside || active === first)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && (outside || active === last)) {
      e.preventDefault()
      first.focus()
    }
  }

  const say = useCallback((text: string) => {
    setMessage(text)
    clearTimeout(messageTimer.current)
    messageTimer.current = setTimeout(() => setMessage(''), 2500)
  }, [])

  // The page URL carrying the open comparison. If the URL cannot be rewritten, the plain page
  // address is what gets copied (and printed): the guest always ends up with a link.
  const shareUrl = () => {
    try {
      return withShareParam(window.location.href, api.selected)
    } catch {
      return window.location.href
    }
  }

  const copyLink = async () => {
    const url = shareUrl()
    let ok = false
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(url)
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) ok = copyByCommand(url, panelRef.current)
    // Both refused: print the link so the guest can take it by hand.
    say(ok ? L.linkCopied : L.copyFailed(url))
  }

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  const shareNatively = async () => {
    const url = shareUrl()
    const data = { title: L.title, text: snaps.map((s) => s.title).filter(Boolean).join(', '), url }
    try {
      if (typeof navigator.canShare === 'function' && !navigator.canShare(data)) {
        await copyLink()
        return
      }
      await navigator.share(data)
    } catch (err) {
      // The guest closing the sheet is an answer, not a failure; any other refusal falls back to Copy link.
      if ((err as { name?: string } | null)?.name === 'AbortError') return
      await copyLink()
    }
  }

  const visibleRows = (list: CompareRow[]) => list.filter((r) => !(api.diffOnly && r.same))
  const anyVisible = visibleRows(rows).length > 0
  const status = message || (api.diffOnly && !anyVisible ? L.allSame : '')

  // #1714: the same resolver the cards use, so a home mapped to its own external page links there from the table too.
  const homeLink = (snap: CompareSnap) => buildGridPropertyUrl(hrefFeed, pdpRoute, snap.slug, options, state.visitorFilters as never)
  const homeHref = (snap: CompareSnap) => homeLink(snap).href
  const linkPropsFor = (snap: CompareSnap) =>
    newTab ? { target: '_blank', rel: 'noopener noreferrer' } : homeLink(snap).external ? { rel: 'noopener' } : {}

  let amenitiesHeaderDone = false
  return (
    <div className="hrc-modal" data-hr-compare-modal="" style={api.accentStyle}>
      <div className="hrc-modal__backdrop" onClick={api.closeTable} />
      <div
        ref={panelRef}
        className="hrc-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="hrc-modal__head">
          <h3 id={titleId} className="hrc-modal__title">{L.title}</h3>
          <label className="hrc-switch">
            <input
              type="checkbox"
              className="hrc-switch__box"
              data-hr-compare-diff=""
              checked={api.diffOnly}
              onChange={(e) => api.setDiffOnly(e.currentTarget.checked)}
            />
            <span className="hrc-switch__track" />
            <span className="hrc-switch__text">{L.differences}</span>
          </label>
          <div className="hrc-modal__actions">
            <button type="button" className="hrc-btn hrc-btn--ghost hrc-modal__share" data-hr-compare-copy="" onClick={copyLink}>
              {L.copyLink}
            </button>
            {canShare ? (
              <button type="button" className="hrc-btn hrc-btn--ghost hrc-modal__share" data-hr-compare-share="" onClick={shareNatively}>
                {L.share}
              </button>
            ) : null}
          </div>
          <button type="button" className="hrc-modal__close" data-hr-compare-close="" aria-label={L.close} onClick={api.closeTable}>
            {'×'}
          </button>
        </div>
        <p className="hrc-modal__status" aria-live="polite" hidden={!status}>{status}</p>
        <div className="hrc-scroller">
          <table className="hrc-table" style={{ ['--hrc-cols' as string]: snaps.length }}>
            <thead>
              <tr>
                <th className="hrc-corner" scope="col"><span className="hrc-sr">{L.title}</span></th>
                {snaps.map((snap) => (
                  <th key={snap.slug} className="hrc-th" scope="col" data-hrc-slug={snap.slug}>
                    <span className="hrc-th__media">
                      {snap.image ? <img src={getProxiedImageUrl(snap.image, 480, 300)} alt="" loading="lazy" /> : null}
                    </span>
                    <span className="hrc-th__name">
                      <a href={homeHref(snap)} {...linkPropsFor(snap)}>{snap.title}</a>
                    </span>
                    <button type="button" className="hrc-th__remove" aria-label={L.removeHome(snap.title)} onClick={() => api.remove(snap.slug)}>
                      {'×'}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const hidden = api.diffOnly && row.same
                let header: React.ReactNode = null
                if (row.group === 'amenities' && !amenitiesHeaderDone) {
                  amenitiesHeaderDone = true
                  const anyAmenity = rows.some((r) => r.group === 'amenities' && !(api.diffOnly && r.same))
                  header = (
                    <tr className="hrc-group" hidden={!anyAmenity}>
                      <th scope="colgroup" colSpan={snaps.length + 1}>{L.amenities}</th>
                    </tr>
                  )
                }
                return (
                  <Fragment key={row.key}>
                    {header}
                    <tr
                      className="hrc-tr"
                      data-row={row.key}
                      data-amenity={row.group === 'amenities' ? row.label : undefined}
                      data-same={row.same ? '1' : '0'}
                      hidden={hidden}
                    >
                      <th scope="row" className="hrc-rh">{row.label}</th>
                      {row.cells.map((cell, i) => {
                        const slug = snaps[i]?.slug
                        if (cell.yes !== undefined) {
                          return (
                            <td key={slug} className="hrc-td hrc-td--mark" data-hrc-slug={slug}>
                              <span className={`hrc-mark ${cell.yes ? 'is-yes' : 'is-no'}`}>
                                {cell.yes ? <CheckIcon /> : '—'}
                                <span className="hrc-sr">{cell.yes ? L.included : L.notListed}</span>
                              </span>
                            </td>
                          )
                        }
                        if (cell.text === '') {
                          return <td key={slug} className="hrc-td is-empty" data-hrc-slug={slug}>{'—'}</td>
                        }
                        return (
                          <td key={slug} className={`hrc-td${row.key === 'sleeps' || row.key === 'bedrooms' || row.key === 'bathrooms' ? ' hrc-td--num' : ''}`} data-hrc-slug={slug}>
                            {row.key === 'price' ? (
                              <span className="hrc-price">
                                <span className="hrc-price__value">{cell.text}</span>
                                {cell.note ? <span className="hrc-price__note">{cell.note}</span> : null}
                              </span>
                            ) : cell.text}
                          </td>
                        )
                      })}
                    </tr>
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="hrc-corner" />
                {snaps.map((snap) => (
                  <td key={snap.slug} className="hrc-tf">
                    <a className="hrc-btn hrc-btn--primary" href={homeHref(snap)} {...linkPropsFor(snap)}>{L.view}</a>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}

// -- the dock --------------------------------------------------------------------------------

/**
 * Everything compare draws outside the grid: the floating pill (while the
 * header toggle is out of view or missing), the tray and the dialog. Mounted
 * once at the explorer root so no draggable sheet or transformed ancestor can
 * take `position: fixed` away from them.
 */
export function CompareDock() {
  const api = useCompare()
  return api ? <CompareDockBody api={api} /> : null
}

function CompareDockBody({ api }: { api: CompareApi }) {
  const { feedId, widgetId } = useWidget<ExploreWidgetConfig>()
  // The PDP route comes from the widget query the cards already read. The observer mounts with the
  // dock, not with the dialog, so opening the table never adds a subscriber to that query: with a
  // client whose data is stale on mount it would otherwise refetch the feed and widget.
  const { pdpRoute } = usePdpRoute(feedId, widgetId)
  // #1714: the External Property Pages slice of the same widget record (stable identity; undefined when unset).
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)
  const [clientReady, setClientReady] = useState(false)
  const openRef = useRef<HTMLButtonElement>(null)
  const trayHost = useRef<HTMLDivElement>(null)
  const lastFocus = useRef<HTMLElement | null>(null)
  const wasOpen = useRef(false)
  const open = api.open

  useEffect(() => { setClientReady(true) }, [])

  // The dialog gives focus back to whatever opened it, else to the tray's Compare button.
  useEffect(() => {
    if (wasOpen.current && !open) {
      const previous = lastFocus.current
      const usable = previous && previous.isConnected && previous !== document.body && previous !== document.documentElement
      const target = usable ? previous : openRef.current
      target?.focus()
      lastFocus.current = null
    }
    wasOpen.current = open
  }, [open])

  const requestOpen = () => {
    lastFocus.current = activeWithin(trayHost.current) ?? null
    api.openTable()
  }

  const showPill = clientReady && !api.mode && api.rowCount >= 2 && !(api.entry.mounted && api.entry.visible)
  return (
    <div ref={trayHost} className="hrc-dock" data-hr-compare-dock="">
      {showPill ? (
        <button type="button" className="hrc-entry hrc-entry--pill" data-hr-compare-pill="" aria-pressed={false} style={api.accentStyle} onClick={() => api.setMode(true)}>
          <ColumnsIcon />
          <span className="hrc-entry__text">{api.buttonText}</span>
        </button>
      ) : null}
      {api.mode ? <CompareTray api={api} openRef={openRef} onOpen={requestOpen} /> : null}
      {api.open ? <CompareDialog api={api} pdpRoute={pdpRoute} hrefFeed={hrefFeed} /> : null}
    </div>
  )
}
